import { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileText, Upload, Download, Eye } from "lucide-react";

export default function Reports() {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [apts, setApts] = useState([]);
  const [title, setTitle] = useState("");
  const [aptId, setAptId] = useState("");
  const [file, setFile] = useState(null);
  const [previewR, setPreviewR] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const [r, a] = await Promise.all([api.get("/reports"), api.get("/appointments")]);
    setReports(r.data);
    setApts(a.data);
  };
  useEffect(() => { load(); }, []);

  const upload = async () => {
    if (!file || !title) { toast.error("Provide title and file"); return; }
    setLoading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post("/reports", {
          title,
          file_base64: reader.result,
          appointment_id: aptId && aptId !== "none" ? aptId : null,
        });
        toast.success("Report uploaded");
        setTitle(""); setFile(null); setAptId("");
        load();
      } catch (e) { toast.error(formatError(e)); }
      finally { setLoading(false); }
    };
    reader.readAsDataURL(file);
  };

  const view = async (r) => {
    const { data } = await api.get(`/reports/${r.id}`);
    setPreviewR(data);
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
      <div className="mb-6">
        <div className="text-xs font-semibold uppercase tracking-widest text-teal-700">Reports</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold mt-1">Diagnostic reports</h1>
        <p className="text-slate-600 text-sm mt-1">
          {user?.role === "doctor" ? "View reports uploaded for your patients." : "Upload PDFs or images. Your doctor can view them from the appointment."}
        </p>
      </div>

      {user?.role !== "doctor" && (
        <Card className="p-5 rounded-2xl mb-6">
          <div className="grid md:grid-cols-4 gap-3 items-end">
            <div>
              <Label>Report title</Label>
              <Input data-testid="report-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. CBC test - Aug 12" />
            </div>
            <div>
              <Label>Link to appointment (optional)</Label>
              <Select value={aptId} onValueChange={setAptId}>
                <SelectTrigger data-testid="report-apt"><SelectValue placeholder="— None —" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {apts.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.doctor_name} · {new Date(a.slot_time).toLocaleDateString()}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>File (PDF/Image)</Label>
              <Input data-testid="report-file" type="file" accept="application/pdf,image/*" onChange={(e) => setFile(e.target.files?.[0])} />
            </div>
            <Button data-testid="upload-report" disabled={loading} onClick={upload} className="bg-teal-600 hover:bg-teal-500 text-white">
              <Upload className="w-4 h-4 mr-2" /> {loading ? "Uploading…" : "Upload"}
            </Button>
          </div>
        </Card>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {reports.length === 0 && (
          <Card className="p-10 rounded-2xl text-center text-slate-500 col-span-full">No reports yet.</Card>
        )}
        {reports.map((r) => (
          <Card key={r.id} className="p-5 rounded-2xl card-lift" data-testid={`report-${r.id}`}>
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl bg-teal-500/15 flex items-center justify-center text-teal-700">
                <FileText className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <div className="font-semibold">{r.title}</div>
                <div className="text-xs text-slate-500 mt-1">{new Date(r.created_at).toLocaleString()}</div>
                {r.appointment_id && <Badge variant="outline" className="mt-2 text-[10px]">Linked to appointment</Badge>}
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <Button data-testid={`view-${r.id}`} size="sm" variant="outline" onClick={() => view(r)}>
                <Eye className="w-4 h-4 mr-1" /> View
              </Button>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={!!previewR} onOpenChange={(o) => !o && setPreviewR(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>{previewR?.title}</DialogTitle></DialogHeader>
          {previewR?.file_base64 && (
            previewR.file_base64.startsWith("data:application/pdf") ? (
              <iframe title="pdf" src={previewR.file_base64} className="w-full h-[70vh] rounded-lg border" />
            ) : (
              <img src={previewR.file_base64} alt={previewR.title} className="w-full max-h-[70vh] object-contain rounded-lg border" />
            )
          )}
          {previewR?.file_base64 && (
            <a href={previewR.file_base64} download={`${previewR.title}.pdf`} className="inline-flex items-center gap-2 text-teal-700 font-semibold mt-2" data-testid="download-report">
              <Download className="w-4 h-4" /> Download
            </a>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
